import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { readComplaintPhotos } from '@/lib/complaintPhotos';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const date = value => value ? new Date(value).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' }) : '-';
const day = value => value ? new Date(value).toLocaleDateString('id-ID', { dateStyle: 'medium' }) : '-';
const roles = { technician: 'Teknisi', housekeeping: 'Housekeeping', admin: 'Admin', superadmin: 'Super Admin' };
const statuses = { new: 'Baru', in_progress: 'Diproses', closed: 'Selesai', resolved: 'Selesai' };
const priorities = { low: 'Rendah', medium: 'Sedang', high: 'Tinggi', emergency: 'Darurat' };
const imageSrc = url => /^(data:|blob:|https?:)/i.test(url) ? url : `${BACKEND_URL}/${url.replace(/^\//, '')}`;

function ComplaintPhoto({ url, label, onOpen }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <p role="alert" className="border rounded-lg p-3 text-xs text-red-700 bg-red-50">{label}: berkas foto tidak tersedia atau tidak dapat dibaca. Unggah ulang file aslinya.</p>;
  return <button type="button" onClick={onOpen} aria-label={`Perbesar ${label.toLowerCase()}`} className="border rounded-lg p-1 hover:border-isk-teal">
    <img src={imageSrc(url)} alt={label} className="w-full h-32 object-contain rounded-lg" onError={() => setFailed(true)} />
  </button>;
}

function Field({ label, children }) {
  return <div className="p-3 rounded-lg bg-gray-50 border border-gray-100 min-w-0">
    <p className="text-xs text-isk-muted mb-1">{label}</p>
    <div className="text-sm text-isk-text whitespace-pre-wrap break-words">{children || '-'}</div>
  </div>;
}

export default function ComplaintSummary({ complaint: c, showProgress = true, photoUploadUrl, photoToken, onPhotoUpdated, onPhotoUploading }) {
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const groups = [
    ['Foto Komplain', readComplaintPhotos([c.photo_urls, c.photo_url])],
    ['Bukti Pengerjaan', readComplaintPhotos([c.resolution_photos, c.proof_photos])],
  ];
  const replacePhoto = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadError(''); setUploadSuccess(false);
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type) || file.size > 10 * 1024 * 1024) {
      setUploadError('Pilih foto JPG, PNG, WEBP, atau GIF, maksimal 10 MB.'); return;
    }
    setUploading(true);
    onPhotoUploading?.(true);
    try {
      const data = new FormData(); data.append('file', file);
      const response = await axios.put(photoUploadUrl, data, { headers: { Authorization: `Bearer ${photoToken}` } });
      onPhotoUpdated?.(response.data);
      setUploadSuccess(true);
    } catch (error) { setUploadError(error.response?.data?.detail || 'Foto gagal disimpan. Silakan coba lagi.'); }
    finally { setUploading(false); onPhotoUploading?.(false); }
  };
  return <div className="space-y-3 mb-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Penghuni">{c.tenant_name || 'Tidak tercatat'}{c.tenant_phone ? `\n${c.tenant_phone}` : ''}</Field>
      <Field label="Gedung dan Kamar">{c.property_name || '-'} · {c.room_number ? `Kamar ${c.room_number}` : 'Area Umum'}</Field>
      <Field label="Kategori">{c.category}</Field>
      <Field label="Lokasi Detail">{c.location_detail}</Field>
      <Field label="Dibuat">{date(c.created_at)}</Field>
      <Field label="Terakhir Diperbarui">{date(c.updated_at)}</Field>
      {c.completed_at && <Field label="Diselesaikan">{date(c.completed_at)}</Field>}
      {showProgress && <>
        <Field label="Status">{statuses[c.status] || c.status}</Field>
        <Field label="Prioritas">{priorities[c.priority] || c.priority}</Field>
        <Field label="Petugas">{c.assigned_to_name || roles[c.assigned_role] || 'Belum ditugaskan'}</Field>
        <Field label="Estimasi Pengerjaan">{day(c.estimated_date)}{c.estimated_time ? ` pukul ${c.estimated_time}` : ''}</Field>
      </>}
      <Field label="Preferensi Waktu">{day(c.preferred_date)}{c.preferred_time ? ` pukul ${c.preferred_time}` : ''}</Field>
      {(c.pic_role || c.worked_at) && <Field label="PIC dan Waktu Pengerjaan">{roles[c.pic_role] || c.pic_role || '-'}{`\n${date(c.worked_at)}`}</Field>}
    </div>
    <Field label="Deskripsi Lengkap">{c.description}</Field>
    {groups.map(([label, photos]) => <div key={label}>
      <p className="text-sm font-medium text-isk-text mb-2">{label}</p>
      {photos.damaged && <p role="alert" className="text-sm text-red-700 bg-red-50 p-3 rounded-lg mb-2">Foto lama tersimpan tidak lengkap dan tidak dapat ditampilkan. Silakan unggah ulang file aslinya.</p>}
      {photos.urls.length ? <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {photos.urls.map((url, index) => <ComplaintPhoto key={url} url={url} label={`${label} ${index + 1}`} onOpen={() => setPhoto({ src: imageSrc(url), label: `${label} ${index + 1}` })} />)}
      </div> : !photos.damaged && <p className="text-xs text-isk-muted">Belum ada foto</p>}
      {label === 'Foto Komplain' && photoUploadUrl && <div className="mt-3">
        <label className="text-sm font-medium text-isk-teal">{uploading ? 'Menyimpan foto...' : 'Unggah / Ganti Foto Komplain'}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" aria-label="Unggah atau ganti foto komplain" disabled={uploading} onChange={replacePhoto} className="block mt-2 w-full text-xs" />
        </label>
        <p className="text-xs text-isk-muted mt-1">Foto yang dipilih akan menggantikan foto komplain sebelumnya. Maksimal 10 MB.</p>
        {uploadError && <p role="alert" className="text-sm text-red-700 mt-2">{uploadError}</p>}
        {uploadSuccess && <p role="status" className="text-sm text-green-700 mt-2">Foto berhasil disimpan.</p>}
      </div>}
    </div>)}
    <Dialog open={!!photo} onOpenChange={open => { if (!open) setPhoto(null); }}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-4xl max-h-[90dvh] overflow-y-auto rounded-2xl bg-white">
        <DialogTitle>{photo?.label || 'Foto komplain'}</DialogTitle>
        <DialogDescription className="sr-only">Pratinjau foto komplain ukuran penuh</DialogDescription>
        {photo && <img src={photo.src} alt={photo.label} className="max-h-[75dvh] w-full object-contain" />}
      </DialogContent>
    </Dialog>
  </div>;
}
